import { Dialog as OriginalDialog } from '@headlessui/react';
import { ReactNode } from 'react';
import cn from 'classnames';

type PanelSizes = 'md' | 'sm' | 'xs';

const PANEL_CLASS: Record<PanelSizes, string> = {
  md: 'max-w-[100%] md:max-w-[75%] lg:max-w-[50%]',
  sm: 'max-w-[100%] md:max-w-[75%] lg:max-w-[50%] xl:max-w-[40%]',
  xs: 'max-w-[100%] sm:max-w-[65%] md:max-w-[40%] lg:max-w-[35%] xl:max-w-[33%] 2xl:max-w-[30%]',
};

const DialogRoot: typeof OriginalDialog.prototype = (
  props: Parameters<typeof OriginalDialog>[0] & {
    size?: PanelSizes;
    hideCloseButton?: boolean;
    roundedSize?: string;
    customPadding?: string;
  },
) => {
  const {
    className,
    hideCloseButton,
    roundedSize,
    customPadding,
    ...otherProps
  } = props;

  return (
    <OriginalDialog
      as="div"
      className={`fixed inset-0 z-50 overflow-y-auto ${className}`}
      {...otherProps}
    >
      <div className="fixed inset-0 bg-gray-700 opacity-50" />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <OriginalDialog.Panel
            className={cn(
              PANEL_CLASS[props.size || 'md'],
              roundedSize || 'rounded-xl xs:rounded-2xl',
              customPadding || 'p-4 sm:p-6',
              ` relative w-full overflow-hidden bg-white text-left align-middle shadow-xl transition-all`,
            )}
          >
            {!hideCloseButton && (
              <button
                onClick={() => props.onClose(true)}
                type="button"
                className="btn-close absolute top-3 right-3 opacity-100 outline-none md:top-4 md:right-4 md:h-4 md:w-4"
                aria-label="Close"
              />
            )}
            {props.children as ReactNode}
          </OriginalDialog.Panel>
        </div>
      </div>
    </OriginalDialog>
  );
};

DialogRoot.displayName = 'DialogContext'; // https://github.com/tailwindlabs/headlessui/blob/main/packages/%40headlessui-react/src/components/dialog/dialog.tsx#L81

const Title: typeof OriginalDialog.Title.prototype = (
  props: Parameters<typeof OriginalDialog.Title>[0],
) => (
  <OriginalDialog.Title
    as="h1"
    className={cn(
      'items-center border-b border-gray-200 px-0 pt-4 pb-5 text-center text-xl font-bold text-gray-700 xs:py-5 xs:px-5 sm:px-10 md:flex-row md:text-3xl',
      props.className,
    )}
  >
    {props.children}
  </OriginalDialog.Title>
);

export const Dialog: typeof OriginalDialog = Object.assign(DialogRoot, {
  Backdrop: OriginalDialog.Backdrop,
  Panel: OriginalDialog.Panel,
  Overlay: OriginalDialog.Overlay,
  Title,
  Description: OriginalDialog.Description,
  size: 'sm',
  hideCloseButton: false,
  roundedSize: 'rounded-2xl',
  customPadding: 'p-4 sm:p-6',
}); // https://github.com/tailwindlabs/headlessui/blob/main/packages/%40headlessui-react/src/components/dialog/dialog.tsx#L544
